import { Fragment, KeyboardEvent, useEffect, useRef, useState } from "react";
import { ExternalArrow, SMark } from "./icons";
import ChatDemo from "./ChatDemo";
import { PageCtx } from "./ctx";
import { asset, AVATARS, HERO_CHIPS, HERO_VIDEO_MP4, HERO_VIDEO_POSTER, HERO_VIDEO_WEBM } from "./data";
import { fill } from "./links";

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The hero's video, masked into the Stippl S by the stylesheet (the poster is the CSS background until it plays). */
function HeroVideo() {
	const ref = useRef<HTMLVideoElement>(null);
	useEffect(() => {
		// A clip that is not muted cannot autoplay, and the server HTML carries no `muted` attribute (see below).
		const video = ref.current;
		if (!video) return;
		video.muted = true;
		Promise.resolve(video.play()).catch(() => {});
	}, []);
	return (
		<div className="hero-s" aria-hidden="true">
			<div className="hero-s-v">
				{/* React never renders the `muted` attribute on the server, so it always "differs" on hydration; the effect above sets it. */}
				<video ref={ref} autoPlay muted loop playsInline poster={HERO_VIDEO_POSTER} suppressHydrationWarning>
					<source src={HERO_VIDEO_WEBM} type="video/webm" />
					<source src={HERO_VIDEO_MP4} type="video/mp4" />
				</video>
			</div>
		</div>
	);
}

export default function Hero({ ctx }: { ctx: PageCtx }) {
	const { name, tr, trA, listingUrl, promptHref, trackCta } = ctx;
	const [askText, setAskText] = useState("");
	// The page ships a prompt of its own before anyone types; once the box has been touched the link follows it.
	const [askTouched, setAskTouched] = useState(false);
	const askGoRef = useRef<HTMLAnchorElement>(null);
	const askRef = useRef<HTMLTextAreaElement>(null);

	/** What the hero button sends: the box, or the default trip; always ending in the "save it to Stippl" ask. */
	const heroPrompt = (value: string): string => {
		let text = value.trim() || tr("hero.ask.default");
		if (!/stippl/i.test(text)) {
			const leadingPlan = new RegExp(`^${escapeRegExp(tr("hero.ask.planWord"))}\\s+`, "i");
			text = fill(tr("hero.ask.wrap"), { prompt: text.replace(leadingPlan, "").replace(/[.!\s]+$/, "") });
		}
		return text;
	};
	const askHref = promptHref(askTouched ? heroPrompt(askText) : tr("hero.ask.initialPrompt"));

	const onAskKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			askGoRef.current?.click();
		}
	};

	const addLink = (
		<a href={listingUrl} target="_blank" rel="noopener" data-where="hero-add" onClick={() => trackCta("hero-add", "add_connector")}>
			{trA("hero.addLink")}
		</a>
	);

	return (
		<section className="hero" aria-labelledby="atp-h1">
			<HeroVideo />
			<div className="wrap hero-grid">
				<div className="hero-copy">
					<div className="pill-row">
						<span className="badge-new">
							<b>{tr("hero.badgeNew")}</b> {tr("hero.badgeText")}
						</span>
					</div>
					<h1 id="atp-h1">
						{tr("hero.h1Prefix")}
						<span className="u">
							{tr("hero.h1Accent")}
							<svg viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true">
								<path d="M2 14 C 50 4, 120 4, 198 12" stroke="#13c892" strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray="2 9" />
							</svg>
						</span>
					</h1>
					<p className="lede">
						{tr("hero.ledePre")}
						<strong>{tr("hero.ledeQuote")}</strong>
						{tr("hero.ledePost")}
					</p>
					<form className="askbox" onSubmit={(e) => e.preventDefault()}>
						<label htmlFor="atp-askq" className="sr">
							{tr("hero.ask.label")}
						</label>
						<textarea
							id="atp-askq"
							ref={askRef}
							rows={2}
							placeholder={tr("hero.ask.placeholder")}
							value={askText}
							onChange={(e) => {
								setAskText(e.target.value);
								setAskTouched(true);
							}}
							onKeyDown={onAskKeyDown}
						/>
						<div className="askbox-row">
							<span className="askbox-hint">
								<SMark />
								{tr("hero.ask.hint")}
							</span>
							<a
								className="btn btn-green"
								ref={askGoRef}
								href={askHref}
								target="_blank"
								rel="noopener"
								data-where="hero"
								onClick={() => trackCta("hero", "open_assistant")}
							>
								{tr("hero.ask.go")}
								<ExternalArrow />
							</a>
						</div>
					</form>
					<p className="popular">
						{tr("hero.popular.label")}{" "}
						{HERO_CHIPS.map((chip) => (
							<Fragment key={chip}>
								<button
									type="button"
									onClick={() => {
										setAskText(tr(`hero.popular.${chip}.prompt`));
										setAskTouched(true);
										trackCta("hero-chip", "open_assistant");
										askRef.current?.focus();
									}}
								>
									{tr(`hero.popular.${chip}.label`)}
								</button>{" "}
							</Fragment>
						))}
					</p>
					<p className="add-line">
						{tr("hero.addLinePre")}
						{addLink}
					</p>
					<div className="avatars">
						<span>
							{AVATARS.map((avatar) => (
								<img key={avatar} src={asset(avatar)} alt="" />
							))}
						</span>
						<span>
							{tr("hero.joinedPre")}
							<b>{tr("hero.joinedBold")}</b>
							{tr("hero.joinedPost")}
						</span>
					</div>
				</div>
				<ChatDemo ctx={ctx} />
			</div>
		</section>
	);
}
